import { SESSION } from '@/constants/constants'
import { uiHelper } from '@/util/ui-helper'
import { facebookPixelService } from '@/services/facebookpixel.service'

// One ad click = channel (?i=), click id (?fbclid=) and landing URL. They are stored and
// replaced together, so ad A's channel/pixel is never mixed with ad B's click.
const AD_CLICK_DAYS = 30

const readCookie = name => {
  const v = uiHelper.getCookie(name)
  return v ? unescape(v) : null
}

const param = (query, key) => {
  const v = query && query[key]
  if (typeof v === 'string' && v !== '') return v
  if (Array.isArray(v) && v[0]) return v[0]
  return new URLSearchParams(window.location.search).get(key) || null
}

// Call before the page's own query handling (it may re-set fbPixelId from the URL).
export function captureAdClick(query) {
  const channelId = param(query, 'i')
  const fbclid = param(query, 'fbclid')
  const storedFbc = readCookie(SESSION.FB_CLICK)
  const storedFbclid = storedFbc
    ? storedFbc
        .split('.')
        .slice(3)
        .join('.')
    : null
  const isNewFbclid = !!fbclid && fbclid !== storedFbclid
  const isNewChannel = !!channelId && channelId !== readCookie(SESSION.CHANNEL_ID)
  if (!isNewFbclid && !isNewChannel) return

  uiHelper.removeCookie(SESSION.FB_PIXEL_CODE)
  uiHelper.removeCookie(SESSION.CHANNEL_ID)
  uiHelper.removeCookie(SESSION.FB_CLICK)
  uiHelper.removeCookie(SESSION.LANDING_URL)
  if (channelId) uiHelper.setCookie(SESSION.CHANNEL_ID, channelId, AD_CLICK_DAYS)
  // click time is captured now, so beapi can tell which ad was clicked last
  if (fbclid) uiHelper.setCookie(SESSION.FB_CLICK, `fb.1.${Date.now()}.${fbclid}`, AD_CLICK_DAYS)
  uiHelper.setCookie(SESSION.LANDING_URL, window.location.href.slice(0, 1024), AD_CLICK_DAYS)
}

// Fields for register / login requests
export function getAdClickParams() {
  return {
    channel_id: readCookie(SESSION.CHANNEL_ID),
    landing_url: readCookie(SESSION.LANDING_URL),
    // our fb.1.<ms>.<fbclid> first, Meta's own _fbc as fallback
    fbc: readCookie(SESSION.FB_CLICK) || readCookie(SESSION.FB_C)
  }
}

const ensureFbq = () => {
  if (window.fbq) return
  ;(function(f, b, e, v, n, t, s) {
    if (f.fbq) return
    n = f.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    }
    if (!f._fbq) f._fbq = n
    n.push = n
    n.loaded = true
    n.version = '2.0'
    n.queue = []
    t = b.createElement(e)
    t.async = true
    t.src = v
    s = b.getElementsByTagName(e)[0]
    s.parentNode.insertBefore(t, s)
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')
}

// If the visitor came through a channel (ad link) with a bound pixel, fire ONLY that pixel.
// Resolves true when handled; false means "fall back to the domain pixels".
export async function initChannelPixel(query) {
  const channelId = param(query, 'i') || readCookie(SESSION.CHANNEL_ID)
  if (!channelId) return false
  let pixelId = null
  try {
    const res = await facebookPixelService.getChannelPixel(channelId)
    pixelId = res && res.success && res.data ? res.data.fb_pixel_id : null
  } catch (e) {
    pixelId = null
  }
  if (!pixelId) return false

  ensureFbq()
  window.fbq('init', pixelId)
  window.fbq('trackSingle', pixelId, 'PageView')
  uiHelper.setCookie(SESSION.FB_PIXEL_CODE, pixelId, AD_CLICK_DAYS)
  const noscript = document.createElement('noscript')
  noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1" />`
  document.body.appendChild(noscript)
  return true
}

// Channel click tracking (feapi POST /channel/{i}/pageview -> channel_click_events and
// channel_unique_clicks). Once per browser session per channel.
export function sendChannelPageview(query) {
  const channelId = param(query, 'i') || readCookie(SESSION.CHANNEL_ID)
  if (!channelId) return
  const guardKey = `pv_sent:${channelId}`
  try {
    if (sessionStorage.getItem(guardKey)) return
  } catch (e) {
    // storage blocked: still send, at most once per page load
  }

  let fingerprint = uiHelper.getCookie('fp_visitor')
  if (!fingerprint) {
    try {
      fingerprint = uiHelper.getFingerprints()
      if (fingerprint) uiHelper.setCookie('fp_visitor', fingerprint, 30)
    } catch (e) {
      fingerprint = null
    }
  }
  if (!fingerprint) return

  facebookPixelService
    .channelPageview(channelId, {
      fingerprint: String(fingerprint).slice(0, 128),
      referrer: document.referrer ? document.referrer.slice(0, 255) : null
    })
    .then(() => {
      try {
        sessionStorage.setItem(guardKey, '1')
      } catch (e) {
        // ignore
      }
    })
    .catch(() => {
      // tracking only — never block the page
    })
}
